import React, { useEffect } from 'react';
import { Col, Container, Row } from 'reactstrap';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import PropTypes from 'prop-types';

import AppBadge from 'components/App/AppBadge';
import AppButton from 'components/App/Button';
import Breadcrumb from 'components/App/Breadcrumb';
import Meta from 'components/App/Meta';
import ModalFlightNote from 'components/Modals/ModalFlightNote';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import RefundLedger from 'components/RefundLedger';

import images from 'assets/images';
// import { ReactComponent as StarSVG } from 'assets/images/star_purple500.svg';
import { ReactComponent as BedSVG } from 'assets/images/bed.svg';
import { ReactComponent as UserSVG } from 'assets/images/user.svg';
import { ReactComponent as UnitPriceSVG } from 'assets/images/unit-price.svg';
import { ReactComponent as AddPictureIcon } from 'assets/images/booking/customer-feedback.svg';
import {
  BOOKING_STATUS,
  LOCATIONS,
  // CANCEL_PENALTY_TYPE,
  OPTION_CANCEL_PENALTY,
  // PLACE_TYPE,
  ROOM_TYPE,
  TRAVEL_TYPE,
} from 'constants/index';
import {
  CANCELLED_BY_LABELS,
  canConfirmManualRefund,
  getCancelConfirmText,
  getCancelOutcomeToast,
} from 'constants/bookingCancel';
import { convertIsoDateToString, showToastError } from 'utils';
import formatMoney from 'utils/formatMoney';
import formatMaskedAccountNumber from 'utils/formatMaskedAccountNumber';

import styles from './BookingDetail.module.scss';

const breadcrumbList = [
  {
    name: 'Danh sách đặt phòng',
    isActive: false,
    link: LOCATIONS.MANAGER_BOOKING,
  },
  {
    name: 'Chi tiết đơn đặt phòng',
    isActive: true,
    link: '',
  },
];

// Mirrors BE payment.refunds[].status (onepay-refund.md §6), the same OnePay vocabulary the
// flight admin uses — kept local here rather than shared, four lines.
const PAYMENT_REFUND_STATUS_LABELS = {
  REQUESTED: 'Đã gửi lệnh',
  APPROVED: 'Đã duyệt',
  PENDING: 'Đang xử lý',
  FAILED: 'Bị từ chối',
};

const PAYMENT_REFUND_STATUS_COLOR = {
  REQUESTED: 'neutral',
  APPROVED: 'success',
  PENDING: 'warning',
  FAILED: 'error',
};

const PAYMENT_REFUND_ACTION_LABELS = {
  CONFIRM_REFUND_BOOKING: 'Xác nhận đã hoàn tiền',
};

// One RefundLedger row from a payment.refunds[] entry (the shared component takes no product
// vocabulary of its own).
const formatPaymentRefund = (refund) => ({
  id: refund.id,
  time: convertIsoDateToString(refund.requestedAt, 'DD/MM/YYYY HH:mm') || '-',
  action: PAYMENT_REFUND_ACTION_LABELS[refund.action] || refund.action,
  amount: formatMoney.format(refund.amount),
  status: {
    label: PAYMENT_REFUND_STATUS_LABELS[refund.status] || refund.status,
    color: PAYMENT_REFUND_STATUS_COLOR[refund.status] || 'neutral',
  },
  result: refund.transactionNo
    ? `Mã giao dịch OnePay ${refund.transactionNo}`
    : (refund.message || '-'),
});

// Mirrors BE otaSync.status (docs/hotellink/booking-sync.md §3/§4.3).
const OTA_SYNC_STATUS_LABELS = {
  SYNCED: 'Đã đẩy sang Hotellink',
  PENDING: 'Đang đẩy sang Hotellink',
  FAILED: 'Chưa đẩy được sang Hotellink',
  SKIPPED: 'Không đẩy sang Hotellink',
};

// Mirrors BE otaSync.lastErrorKind.
// otaSync.cancel: the Cancel of a booking Hotellink holds (booking-sync.md §6.3).
const OTA_CANCEL_STATUS_LABELS = {
  SYNCED: 'Đã huỷ bên Hotellink',
  PENDING: 'Đang gửi lệnh huỷ, tự thử lại',
  FAILED: 'Chưa huỷ được bên Hotellink, kiểm tra với khách sạn',
};

const OTA_SYNC_ERROR_KIND_LABELS = {
  CONFIG: 'Lỗi cấu hình',
  CONNECT: 'Không kết nối được',
  AMBIGUOUS: 'Không rõ kết quả, kiểm tra tay với khách sạn',
  PARTNER: 'Hotellink từ chối',
  GAVE_UP: 'Quá 24 giờ',
  PAST_CHECKIN: 'Đã qua ngày nhận phòng',
  NO_OTA_HOTEL: 'Chỗ nghỉ chưa map Hotellink',
  NO_OTA_ROOM: 'Phòng chưa map Hotellink',
  RATE_LIMITED: 'Hotellink bận, sẽ tự thử lại',
};

// PENDING also carries the kind/text of its last (failed) attempt while it keeps retrying
// (docs/hotellink/booking-sync.md §4.1 retryLater); shown the same way FAILED/SKIPPED do.
const formatOtaSyncStatusLabel = (otaSync) => {
  const label = OTA_SYNC_STATUS_LABELS[otaSync.status] || otaSync.status;
  return otaSync.status === 'PENDING' ? `${label} (lần ${otaSync.attempts})` : label;
};

function BookingDetail(props) {
  const {
    // isLoadingApproveBooking,
    isLoadingCancelBooking,
    isLoadingRefundBooking,
    isLoadingOtaSyncBooking,
    bookingDetail,
    actionGetBookingDetail,
    // actionApproveBooking,
    actionCancelBooking,
    actionRefundBooking,
    actionOtaSyncBooking,
  } = props;
  const { id } = useParams();
  const { t } = useTranslation();

  const {
    value: isOpenModalCancelBooking,
    setTrue: setOpenModalCancelBooking,
    setFalse: setCloseModalCancelBooking,
    toggle: toggleModalCancelBooking,
  } = useBoolean(false);

  const {
    value: isOpenModalRefundBooking,
    setTrue: setOpenModalRefundBooking,
    setFalse: setCloseModalRefundBooking,
  } = useBoolean(false);

  const {
    value: isOpenModalOtaSync,
    setTrue: setOpenModalOtaSync,
    setFalse: setCloseModalOtaSync,
  } = useBoolean(false);

  // Increments (never toggles) so RefundMethodField can re-force manual on a repeat rejection —
  // same reasoning as the flight admin (onepay-refund.md §3.5).
  const [forceManualOn, setForceManualOn] = React.useState(0);

  // const onApproveBooking = React.useCallback(() => {
  //   actionApproveBooking({ id });
  // }, [actionApproveBooking, id]);

  // Cancelling a paid booking also refunds it (WP-BE-CANCEL-REFUND), and the response is the raw
  // booking, not the detail DTO: the refund ledger and the flags only come back from a refetch.
  const onCancelled = React.useCallback((data) => {
    const { type, text } = getCancelOutcomeToast(data);
    if (type === 'warning') toast.warn(text);
    else toast.success(text);
    actionGetBookingDetail(id);
  }, [actionGetBookingDetail, id]);

  const onCancelBooking = React.useCallback(() => {
    actionCancelBooking({ id, callback: setCloseModalCancelBooking, onCancelled });
  }, [actionCancelBooking, id, onCancelled, setCloseModalCancelBooking]);

  // REFUND_REJECTED / PAYMENT_UNAVAILABLE: the PUT answers the raw booking document, not the
  // detail DTO, so the ledger and the flags only ever come from a refetch, never from this
  // response (confirmed against the real controller run, onepay-refund.md §6).
  const onRefundError = React.useCallback((error) => {
    const data = error.response?.data || {};
    if (data.key === 'REFUND_REJECTED') {
      showToastError(
        `OnePay từ chối lệnh hoàn (mã ${data.responseCode}): ${data.onePayMessage}. `
        + `Đã duyệt ${formatMoney.format(data.approvedAmount)}, `
        + `còn lại ${formatMoney.format(data.remainingAmount)}.`,
      );
      setForceManualOn((n) => n + 1);
      return;
    }
    if (data.key === 'PAYMENT_UNAVAILABLE') {
      // A REQUESTED ledger entry is written either way; refetch so it shows.
      showToastError('OnePay không trả lời, bấm lại để kiểm tra');
      actionGetBookingDetail(id);
    }
  }, [actionGetBookingDetail, id]);

  const onConfirmRefund = React.useCallback((body) => {
    actionRefundBooking({
      id,
      ...body,
      onRefundError,
      callback: () => {
        setCloseModalRefundBooking();
        setForceManualOn(0);
        actionGetBookingDetail(id);
      },
    });
  }, [
    actionRefundBooking, actionGetBookingDetail, id, onRefundError, setCloseModalRefundBooking,
  ]);

  const onCloseRefundModal = React.useCallback(() => {
    setCloseModalRefundBooking();
    setForceManualOn(0);
  }, [setCloseModalRefundBooking]);

  // A booking the place cancelled has no bank account of the customer, so the manual-refund button
  // can no longer sit inside the account block (WP-ADM-CANCEL-REFUND).
  const hasBankAccount = bookingDetail.bankAccounts?.length > 0;
  const isCancelledWithRefund = [
    BOOKING_STATUS.CANCELLED_BY_USER.key, 'WAITING_FOR_REFUND', 'REFUNDED',
  ].includes(bookingDetail.bookingStatus);
  const canConfirmRefund = canConfirmManualRefund(bookingDetail.bookingStatus);

  // null -> ModalFlightNote falls back to manual-only, no OnePay radio (onepay-refund.md §6).
  const refundField = bookingDetail.canRefundThroughOnePay ? {
    amountLabel: formatMoney.format(bookingDetail.refundAmount),
    hasPendingRefund: !!bookingDetail.hasPendingRefund,
    forceManualOn,
  } : null;

  // The endpoint answers only { _id, orderId, otaSync }, never otaBookingId even on SYNCED, so
  // a refetch (not a merge) is the only way the block ever shows a fresh otaBookingId too
  // (docs/hotellink/booking-sync.md §4.3).
  const onOtaSync = React.useCallback(() => {
    actionOtaSyncBooking({
      id,
      callback: () => {
        setCloseModalOtaSync();
        actionGetBookingDetail(id);
      },
    });
  }, [actionOtaSyncBooking, actionGetBookingDetail, id, setCloseModalOtaSync]);

  // NO_OTA_HOTEL is not a problem: every paid booking of a hotel not mapped to Hotellink lands
  // here, so the block would just be noise (PM, WP-ADM-OTA-SYNC). A booking with otaBookingId
  // but no otaSync predates this package (synced through the old code path) — treated as an
  // implicit SYNCED so the block has safe fields to read instead of throwing on otaSync.status.
  // Gated on data presence only: BE already strips otaSync for non-intranet admins, which is
  // the real authority — a client-side isIntranet read off localStorage LOGIN_TYPE went stale
  // for an intranet admin who had opened a location's extranet view (WP-ADM-OTA-SYNC-3).
  const { otaSync, otaBookingId } = bookingDetail;
  const displayOtaSync = otaSync || (otaBookingId ? {
    status: 'SYNCED', attempts: 0, lastTriedAt: null, lastErrorKind: null, lastError: null,
  } : null);
  const showOtaSyncBlock = !!displayOtaSync && displayOtaSync.lastErrorKind !== 'NO_OTA_HOTEL';

  // A cancel Hotellink has not taken yet: the same endpoint resends it instead of a New.
  const otaCancelWaiting = !!otaBookingId
    && ['PENDING', 'FAILED'].includes(otaSync?.cancel?.status);
  const canRetryOtaSync = otaCancelWaiting || (['FAILED', 'SKIPPED'].includes(otaSync?.status)
    && !otaBookingId
    && ['PAID', 'PROCESSING'].includes(bookingDetail.bookingStatus)
    && new Date(bookingDetail.startedAt).setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0));

  // const isStartedAtToday = React.useMemo(() => {
  //   const today = new Date();
  //   return (
  //     today.setHours(0, 0, 0, 0) <=
  //     new Date(bookingDetail.startedAt).setHours(0, 0, 0, 0)
  //   );
  // }, [bookingDetail.startedAt]);

  useEffect(() => {
    if (id) {
      actionGetBookingDetail(id);
    }
  }, [actionGetBookingDetail, id]);

  return (
    <div className={`page-content ${styles.CustomPage}`}>
      <Meta name="Chi tiết đơn đặt phòng" />

      <Container fluid>
        <Breadcrumb
          pageTitle="CHI TIẾT ĐƠN ĐẶT PHÒNG"
          breadcrumbList={breadcrumbList}
        />
        <div className={styles.Page}>
          <div className="row FixSpacingRow">
            <Col lg="12" xl="8" className={styles.ColLeft}>
              {/* <div className="RoomPreview">
                <div className="RoomPreviewImageWrapper">
                  <img className="RoomPreviewImage"
                  src={bookingDetail.placeInfo?.previewImage} alt="avatar" />
                </div>

                <div className="RoomPreviewContent">
                  <div className="RoomPreviewContentTitle">
                    <span className="RoomPreviewContentTitle__Name">
                      {bookingDetail.placeInfo?.name}
                    </span>
                    <span className="RoomPreviewContentTitle__Badge">
                      Mở/ Có thể đặt phòng
                    </span>
                  </div>
                  <div className="RoomPreviewContentMiddle">
                    <span className="RoomPreviewContentMiddle__Tag">
                      {PLACE_TYPE[bookingDetail.placeInfo.placeType]?.label}
                    </span>
                    <div className="RoomPreviewContentMiddle__Rating">
                      {Array.from(Array(bookingDetail.placeInfo.starRating)
                        .keys()).map((item) => (
                          <StarSVG key={item} />
                      ))}
                    </div>
                  </div>
                  <span className="RoomPreviewContentDesc">
                    {bookingDetail.placeInfo?.address}
                  </span>
                </div>
              </div> */}
              <div className="Information">
                <div className="InformationTitleWrapper">
                  <div className="InformationTitle">
                    <div className="InformationTitle__Icon">
                      <BedSVG />
                    </div>
                    <h2>Thông tin đơn đặt chỗ</h2>
                  </div>

                  {/* <div
                    className={`InformationTitle__Status
                    ${BOOKING_STATUS[bookingDetail.bookingStatus]?.key}`}
                  >
                    {BOOKING_STATUS[bookingDetail.bookingStatus]?.label}
                  </div> */}
                </div>

                <div className="InformationWrapper">
                  <div className="InformationItem">
                    <p className="InformationLabel">Mã đặt phòng:</p>
                    <span className="InformationValue">
                      {bookingDetail.orderId}
                    </span>
                  </div>

                  <div className="InformationItem">
                    <p className="InformationLabel">Tình trạng:</p>
                    <span className="InformationValue">
                      {BOOKING_STATUS[bookingDetail.bookingStatus]?.label}
                    </span>
                  </div>

                  {/* Absent on bookings cancelled before WP-BE-CANCEL-REFUND. */}
                  {!!CANCELLED_BY_LABELS[bookingDetail.cancelledBy] && (
                    <div className="InformationItem">
                      <p className="InformationLabel">Huỷ bởi:</p>
                      <span className="InformationValue">
                        {CANCELLED_BY_LABELS[bookingDetail.cancelledBy]}
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <div className="InformationContent InformationBackground">
                    <Col>
                      <div className="InformationLabel">
                        Thời gian nhận phòng dự kiến
                      </div>
                    </Col>
                    <Col>
                      <div className="InformationValue">
                        {bookingDetail.estimatedCheckInAt}
                      </div>
                    </Col>
                  </div>

                  <div className="InformationContent">
                    <Col>
                      <div className="InformationLabel">Ngày nhận phòng</div>
                    </Col>
                    <Col>
                      <div className="InformationValue">
                        {convertIsoDateToString(bookingDetail.startedAt)}
                      </div>
                    </Col>
                  </div>

                  <div className="InformationContent InformationBackground">
                    <Col>
                      <div className="InformationLabel">Ngày trả phòng</div>
                    </Col>
                    <Col>
                      <div className="InformationValue">
                        {convertIsoDateToString(bookingDetail.endedAt)}
                      </div>
                    </Col>
                  </div>
                  <div className="InformationContent">
                    <Col>
                      <div className="InformationLabel">Số phòng</div>
                    </Col>
                    <Col>
                      <div className="InformationValue">
                        {bookingDetail?.rooms?.reduce(
                          (acc, room) => acc + room.roomQuantity,
                          0,
                        )}
                        {' '}
                        phòng
                      </div>
                    </Col>
                  </div>
                  <div className="InformationContent InformationBackground">
                    <Col>
                      <div className="InformationLabel">Người lớn</div>
                    </Col>
                    <Col>
                      <div className="InformationValue">
                        {bookingDetail.adults}
                      </div>
                    </Col>
                  </div>
                  <div className="InformationContent">
                    <Col>
                      <div className="InformationLabel">Trẻ em</div>
                    </Col>
                    <Col>
                      <div className="InformationValue">
                        {bookingDetail.children}
                      </div>
                    </Col>
                  </div>
                </div>

                {/* <div className="InformationWrapper">
                  <Col>
                    <div className="InformationLabel">
                      Chính sách huỷ đặt phòng
                    </div>
                  </Col>
                  <Col>
                    <div className="InformationValue">
                      {bookingDetail.cancelBookingPolicy?.freeBeforeDays && (
                        <div>{`Ngày hủy miễn phí -
                        ${bookingDetail.cancelBookingPolicy.freeBeforeDays}`}</div>
                      )}
                      {bookingDetail.cancelBookingPolicy?.cancelPenalty && (
                        <div>{`Phạt hủy phòng -
                        ${CANCEL_PENALTY_TYPE[bookingDetail
                        .cancelBookingPolicy.cancelPenalty]?.label}`}</div>
                      )}
                    </div>
                  </Col>
                </div> */}

                {/* <div className="InformationSpace" /> */}
              </div>

              <div className="UserInformation">
                <div className="UserInformationTitle">
                  <div className="UserInformationTitle__Icon">
                    <UserSVG />
                  </div>
                  <h2>Thông tin khách đặt chỗ</h2>
                </div>

                <div className="UserInformationWrapper">
                  <Col>
                    <div className="UserInformationLabel">Họ và Tên</div>
                  </Col>
                  <Col>
                    <div className="UserInformationValue">
                      {bookingDetail.userInfo?.fullName}
                    </div>
                  </Col>
                </div>

                <div className="UserInformationWrapper">
                  <Col>
                    <div className="UserInformationLabel">Số điện thoại</div>
                  </Col>
                  <Col>
                    <div className="UserInformationValue">
                      {bookingDetail.userInfo?.phoneNumber}
                    </div>
                  </Col>
                </div>

                <div className="UserInformationWrapper">
                  <Col>
                    <div className="UserInformationLabel">Xuất hoá đơn VAT</div>
                  </Col>
                  <Col>
                    <div className="UserInformationValue">
                      {bookingDetail.isGetInvoice ? 'Có' : 'Không'}
                    </div>
                  </Col>
                </div>

                {Boolean(
                  bookingDetail.invoiceInfo
                  && (bookingDetail.isGetInvoice
                    || bookingDetail.invoiceInfo.organizationName
                    || bookingDetail.invoiceInfo.taxCode),
                ) && (
                  <>
                    <div className="UserInformationWrapper">
                      <Col>
                        <div className="UserInformationLabel">Tên công ty</div>
                      </Col>
                      <Col>
                        <div className="UserInformationValue">
                          {bookingDetail.invoiceInfo?.organizationName}
                        </div>
                      </Col>
                    </div>

                    <div className="UserInformationWrapper">
                      <Col>
                        <div className="UserInformationLabel">Mã số thuế</div>
                      </Col>
                      <Col>
                        <div className="UserInformationValue">
                          {bookingDetail.invoiceInfo?.taxCode}
                        </div>
                      </Col>
                    </div>

                    <div className="UserInformationWrapper">
                      <Col>
                        <div className="UserInformationLabel">
                          Địa chỉ công ty
                        </div>
                      </Col>
                      <Col>
                        <div className="UserInformationValue">
                          {bookingDetail.invoiceInfo?.address}
                        </div>
                      </Col>
                    </div>

                    <div className="UserInformationWrapper">
                      <Col>
                        <div className="UserInformationLabel">
                          Email nhận hoá đơn
                        </div>
                      </Col>
                      <Col>
                        <div className="UserInformationValue">
                          {bookingDetail.invoiceInfo?.email}
                        </div>
                      </Col>
                    </div>
                  </>
                )}

                <div className="UserInformationWrapper">
                  <Col>
                    <div className="UserInformationLabel">Địa chỉ Email</div>
                  </Col>
                  <Col>
                    <div className="UserInformationValue d-inline-flex align-items-center">
                      {bookingDetail.userInfo?.email}
                      {/* false only; undefined/null is a legacy, verified account. */}
                      {bookingDetail.userInfo?.emailVerified === false && (
                        <AppBadge color="warning" className="ms-2">
                          {t('global.email_not_verified')}
                        </AppBadge>
                      )}
                    </div>
                  </Col>
                </div>

                {/* <div className="UserInformationWrapper">
                  <Col>
                    <div className="UserInformationLabel">Số điện thoại</div>
                  </Col>
                  <Col>
                    <div className="UserInformationValue">
                      {bookingDetail.userInfo?.phoneNumber}
                    </div>
                  </Col>
                </div> */}

                <div className="UserInformationWrapper">
                  <Col>
                    <div className="UserInformationLabel">
                      Mục đích chuyến đi
                    </div>
                  </Col>
                  <Col>
                    <div className="UserInformationValue">
                      {TRAVEL_TYPE[bookingDetail.tripPurpose]?.label}
                    </div>
                  </Col>
                </div>

                <div className="UserInformationWrapper">
                  <Col>
                    <div className="UserInformationLabel">
                      Thành phố sinh sống
                    </div>
                  </Col>
                  <Col>
                    <div className="UserInformationValue">
                      {bookingDetail.userInfo?.province}
                    </div>
                  </Col>
                </div>

                <div className="UserInformationWrapper">
                  <Col>
                    <div className="UserInformationLabel">Yêu cầu đặc biệt</div>
                  </Col>
                  <Col>
                    <div className="UserInformationValue">
                      {bookingDetail.note}
                    </div>
                  </Col>
                </div>
              </div>

              <div className="UnitPriceInformation">
                <div className="UnitPriceInformationTitle">
                  <div className="UnitPriceInformationTitle__Icon">
                    <UnitPriceSVG />
                  </div>
                  <h2>Đơn giá</h2>
                </div>

                {bookingDetail.rooms.map((room) => (
                  <div className="UnitPriceInformationContent" key={room._id}>
                    <div className="UnitPriceInformationWrapper">
                      <Col>
                        <div className="UnitPriceInformationLabel">
                          {room.parentRoomName
                            ? ROOM_TYPE[room.parentRoomName]?.label
                            : room.name}
                        </div>
                      </Col>
                      <Col>
                        <div className="UnitPriceInformationValue">
                          x
                          {room.roomQuantity}
                        </div>
                      </Col>
                    </div>

                    <div className="UnitPriceInformationWrapper">
                      <Col>
                        <div className="UnitPriceInformationLabel">
                          Giá phòng
                        </div>
                      </Col>
                      <Col>
                        <div className="UnitPriceInformationValue">
                          {formatMoney.format(room.roomPrice)}
                        </div>
                      </Col>
                    </div>
                  </div>
                ))}

                <div className="DiscountInformation">
                  <div className="DiscountInformationItem">
                    <Col>
                      <div className="UnitPriceInformationLabel">Mã giảm giá</div>
                    </Col>
                    <Col>

                      <div className="UnitPriceInformationValue">
                        {`- ${formatMoney.format(bookingDetail.voucherInfo?.discount)}`}
                      </div>
                    </Col>
                  </div>

                  <div className="DiscountInformationItem">
                    <Col>
                      <div className="UnitPriceInformationLabel">Giảm giá từ xu</div>
                    </Col>
                    <Col>

                      <div className="UnitPriceInformationValue">
                        {`- ${formatMoney.format(bookingDetail.coin)}`}
                      </div>
                    </Col>
                  </div>

                </div>

                <div className="UnitPriceInformationWrapper">
                  <Col>
                    <div className="UnitPriceInformationLabel">Tổng giá</div>
                  </Col>
                  <Col>
                    {/* {!!bookingDetail.totalDiscountedPrice && (
                      <div className="UnitPriceInformationValue
                      UnitPriceInformationDiscountValue FontSemiBold"
                      >
                        {formatMoney.format(bookingDetail.totalPrice)}
                      </div>
                    )} */}

                    <div className="UnitPriceInformationValue FontSemiBold">
                      {formatMoney.format(
                        bookingDetail.totalDiscountedPrice ||
                          bookingDetail.totalPrice,
                      )}
                    </div>
                  </Col>
                </div>

                {bookingDetail.bookingStatus ===
                  BOOKING_STATUS.CANCELLED_BY_USER.key && (
                  <div className="UnitPriceRefundWrapper LineTop">
                    <Row>
                      <Col>
                        <div className="UnitPriceInformationLabel">
                          Hoàn tiền
                        </div>
                      </Col>
                      <Col>
                        {
                          OPTION_CANCEL_PENALTY[
                            bookingDetail.cancelBookingPolicy?.cancelPenalty
                          ]?.label
                        }
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      <Col>
                        <div className="UnitPriceInformationLabel">
                          Giá tiền thanh toán
                        </div>
                      </Col>
                      <Col>
                        <div className="UnitPriceInformationValue TextColorRed FontSemiBold">
                          {formatMoney.format(bookingDetail.amountHaveToPay)}
                        </div>
                      </Col>
                    </Row>
                  </div>
                )}
              </div>
            </Col>

            <Col lg="12" xl="4" className={styles.ColRight}>
              {isCancelledWithRefund && hasBankAccount && (
                <div className="CustomerWrapper">
                  <h1 className="CustomerWrapperTitle">
                    TÀI KHOẢN HOÀN TIỀN CỦA KHÁCH
                  </h1>

                  <div className="CustomerWrapperFieldContainer">
                    <div>
                      <p className="CustomerWrapperFieldLabel">
                        Tên ngân hàng
                      </p>

                      <p className="CustomerWrapperFieldValue">
                        {bookingDetail.bankAccounts[0].bankName}
                      </p>
                    </div>

                    <div>
                      <p className="CustomerWrapperFieldLabel">
                        Số tài khoản
                      </p>

                      <p className="CustomerWrapperFieldValue">
                        {formatMaskedAccountNumber(
                          bookingDetail.bankAccounts[0].accountNumber,
                        )}
                      </p>
                    </div>

                    <div>
                      <p className="CustomerWrapperFieldLabel">Chủ thẻ</p>

                      <p className="CustomerWrapperFieldValue">
                        {bookingDetail.bankAccounts[0].accountName}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {canConfirmRefund && (
                <>
                  <AppButton
                    classes={`w-100 mb-3 ${styles.BtnRefund}`}
                    loading={isLoadingRefundBooking}
                    onClick={setOpenModalRefundBooking}
                    disabled={bookingDetail.bookingStatus === 'REFUNDED'}
                  >
                    Xác nhận đã hoàn tiền
                  </AppButton>

                  {!hasBankAccount && bookingDetail.bookingStatus === 'WAITING_FOR_REFUND' && (
                    <p className={styles.RefundHint}>
                      Khách chưa cung cấp tài khoản, liên hệ khách trước khi hoàn thủ công.
                    </p>
                  )}

                  <ModalFlightNote
                    isOpen={isOpenModalRefundBooking}
                    title="Xác nhận đã hoàn tiền"
                    refundField={refundField}
                    gatewayRefOptional
                    isLoading={isLoadingRefundBooking}
                    onConfirm={onConfirmRefund}
                    onClose={onCloseRefundModal}
                  />
                </>
              )}

              {isCancelledWithRefund && hasBankAccount && (
                <div className="CustomerWrapper CustomerFeedbackWrapper">
                  <h1 className="CustomerWrapperTitle">
                    PHẢN HỒI CỦA KHÁCH HÀNG
                  </h1>

                  {bookingDetail.cancelReasons.length > 0 ? (
                    <div className="CustomerFeedbackWrapperData">
                      {bookingDetail.cancelReasons.map((reason) => (
                        <span key={reason}>{reason}</span>
                      ))}
                    </div>
                  ) : (
                    <div className="CustomerFeedbackWrapperEmpty">
                      <p>Hiện chưa có đánh giá</p>

                      <AddPictureIcon width="3rem" height="3rem" />
                    </div>
                  )}
                </div>
              )}

              {(bookingDetail.refunds || []).length > 0 && (
                <div className="CustomerWrapper">
                  <h1 className="CustomerWrapperTitle">Lệnh hoàn OnePay</h1>

                  <RefundLedger rows={bookingDetail.refunds.map(formatPaymentRefund)} />
                </div>
              )}

              {showOtaSyncBlock && (
                <div className="CustomerWrapper">
                  <h1 className="CustomerWrapperTitle">Hotellink</h1>

                  <div className="CustomerWrapperFieldContainer">
                    <div>
                      <p className="CustomerWrapperFieldLabel">Trạng thái</p>

                      <p className="CustomerWrapperFieldValue">
                        {formatOtaSyncStatusLabel(displayOtaSync)}
                      </p>
                    </div>

                    {displayOtaSync.status === 'SYNCED' && (
                      <div>
                        <p className="CustomerWrapperFieldLabel">Mã đặt phòng Hotellink</p>

                        <p className="CustomerWrapperFieldValue">{otaBookingId}</p>
                      </div>
                    )}

                    <div>
                      <p className="CustomerWrapperFieldLabel">Số lần thử</p>

                      <p className="CustomerWrapperFieldValue">{displayOtaSync.attempts}</p>
                    </div>

                    <div>
                      <p className="CustomerWrapperFieldLabel">Lần thử gần nhất</p>

                      <p className="CustomerWrapperFieldValue">
                        {convertIsoDateToString(displayOtaSync.lastTriedAt, 'DD/MM/YYYY HH:mm')
                          || '-'}
                      </p>
                    </div>

                    {displayOtaSync.cancel?.status && (
                      <div>
                        <p className="CustomerWrapperFieldLabel">Huỷ bên Hotellink</p>

                        <p className="CustomerWrapperFieldValue">
                          {OTA_CANCEL_STATUS_LABELS[displayOtaSync.cancel.status]
                            || displayOtaSync.cancel.status}
                          {displayOtaSync.cancel.status !== 'SYNCED' && displayOtaSync.cancel.lastError
                            ? ` (${displayOtaSync.cancel.lastError})`
                            : ''}
                        </p>
                      </div>
                    )}

                    {displayOtaSync.lastErrorKind && (
                      <div>
                        <p className="CustomerWrapperFieldLabel">
                          {OTA_SYNC_ERROR_KIND_LABELS[displayOtaSync.lastErrorKind]
                            || displayOtaSync.lastErrorKind}
                        </p>

                        <p className="CustomerWrapperFieldValue">{displayOtaSync.lastError}</p>
                      </div>
                    )}
                  </div>

                  {canRetryOtaSync && (
                    <AppButton
                      classes={`w-100 mt-3 ${styles.BtnCancer}`}
                      loading={isLoadingOtaSyncBooking}
                      onClick={setOpenModalOtaSync}
                    >
                      {otaCancelWaiting ? 'Gửi lại lệnh huỷ sang Hotellink' : 'Đẩy lại Hotellink'}
                    </AppButton>
                  )}

                  <ModalRequestAction
                    isOpen={isOpenModalOtaSync}
                    icon={images.modalWarning.default}
                    textRequest={otaCancelWaiting
                      ? 'Bạn có chắc chắn muốn gửi lại lệnh huỷ đặt phòng này sang Hotellink không?'
                      : 'Bạn có chắc chắn muốn đẩy lại đặt phòng này sang Hotellink không?'}
                    onCloseModal={setCloseModalOtaSync}
                    onConfirm={onOtaSync}
                    isLoadingConfirm={isLoadingOtaSyncBooking}
                  />
                </div>
              )}
            </Col>
          </div>

          {(bookingDetail.bookingStatus === BOOKING_STATUS.PENDING.key ||
            bookingDetail.bookingStatus === BOOKING_STATUS.MODIFIED.key ||
            bookingDetail.bookingStatus === BOOKING_STATUS.PAID.key) && (
              <div className={`${styles.FooterButton}`}>
                <AppButton
                  classes={styles.BtnCancer}
                  onClick={setOpenModalCancelBooking}
                  loading={isLoadingCancelBooking}
                >
                  Hủy đặt phòng
                </AppButton>
                {/* {!(
                  bookingDetail.bookingStatus === BOOKING_STATUS.PENDING.key ||
                  bookingDetail.bookingStatus === BOOKING_STATUS.PAID.key
                ) && (
                  <AppButton
                    loading={isLoadingApproveBooking}
                    onClick={onApproveBooking}
                  >
                    Xác nhận
                  </AppButton>
                )} */}

                <ModalRequestAction
                  isOpen={isOpenModalCancelBooking}
                  icon={images.modalGarbageCan.default}
                  textRequest={getCancelConfirmText(bookingDetail.isPaid)}
                  onToggle={toggleModalCancelBooking}
                  onCloseModal={setCloseModalCancelBooking}
                  onConfirm={onCancelBooking}
                />
              </div>
          )}
        </div>
      </Container>
    </div>
  );
}

BookingDetail.propTypes = {
  // isLoadingApproveBooking: PropTypes.bool.isRequired,
  isLoadingCancelBooking: PropTypes.bool.isRequired,
  isLoadingRefundBooking: PropTypes.bool.isRequired,
  isLoadingOtaSyncBooking: PropTypes.bool.isRequired,

  bookingDetail: PropTypes.instanceOf(Object),
  actionGetBookingDetail: PropTypes.func.isRequired,
  // actionApproveBooking: PropTypes.func.isRequired,
  actionCancelBooking: PropTypes.func.isRequired,
  actionRefundBooking: PropTypes.func.isRequired,
  actionOtaSyncBooking: PropTypes.func.isRequired,
};

BookingDetail.defaultProps = {
  bookingDetail: {},
};

export default BookingDetail;
